import {
  AddOutlined,
  ArticleOutlined,
  CloseOutlined,
  DeleteOutline,
  FlagOutlined,
  RestoreOutlined,
} from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAutocomplete, type UseAutocompleteReturnType } from '@refinedev/mui';
import { Fragment, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { DOCUMENT_TYPES } from 'shiver-core/static';
import { uuidv7 } from 'uuidv7';
import { ReadOnlyField } from '../../../components/fields/ReadOnlyField.tsx';
import {
  MilestoneId,
  type Milestone,
  type Timeline,
  type TimelineDocumentHead,
} from '../../../types/index.ts';

// 編集用に最適化したMilestone
type ManagedMilestone = Milestone & { disabled: boolean };

export const MilestoneListField = () => {
  const { watch, setValue } = useFormContext<Timeline, 'milestones'>();

  const timelineId = watch('id');
  const rawItems = watch('milestones');
  const items = useMemo(() => parseManagedMilestones(rawItems), [rawItems]);

  const setItems = (newItems: Array<ManagedMilestone>) => {
    setValue(
      'milestones',
      newItems.filter((item) => !item.disabled),
    );
  };

  const enforcements = useMemo(() => items.filter((item) => item.type === 'enforcement'), [items]);
  const lastEnforcementIndex = useMemo(
    () => items.findLastIndex((item) => item.type === 'enforcement'),
    [items],
  );

  const handleChange = (item: ManagedMilestone) => {
    setItems(items.map((prev) => (prev.id === item.id ? item : prev)));
  };

  const handleDelete = (item: ManagedMilestone) => {
    if (item.type === 'diet') {
      setItems(items.map((prev) => (prev.id === item.id ? { ...prev, disabled: true } : prev)));
    } else if (item.type === 'enforcement') {
      if (enforcements.length > 1) {
        const index = items.findIndex(({ id }) => id === item.id);
        setItems(items.filter((_, i) => i !== index && i !== index - 1));
      }
    }
  };

  const handleRestore = (item: ManagedMilestone) => {
    if (item.type === 'diet') {
      setItems(items.map((prev) => (prev.id === item.id ? { ...prev, disabled: false } : prev)));
    }
  };

  const documentAutocomplete = useAutocomplete<TimelineDocumentHead>({
    resource: `contents/documents`,
    filters: [
      {
        field: 'timelineId',
        operator: 'eq',
        value: timelineId ?? 'none',
      },
    ],
    sort: [
      {
        field: 'title',
        order: 'asc',
      },
    ],
    onSearch: (value) => [
      {
        field: 'title',
        operator: 'contains',
        value,
      },
    ],
  });

  const selectedDocumentIds = useMemo(
    () => new Set(items.flatMap((item) => item.documents.map((doc) => doc.id))),
    [items],
  );

  const filteredDocumentAutoComplete = useMemo(
    () => ({
      ...documentAutocomplete,
      autocompleteProps: {
        ...documentAutocomplete.autocompleteProps,
        // すでに選択済みのものは選択肢に出さない
        options: documentAutocomplete.autocompleteProps.options.filter(
          (item) => !selectedDocumentIds.has(item.id),
        ),
      },
    }),
    [documentAutocomplete, selectedDocumentIds],
  );

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      {items.map((item, i) => (
        <Fragment key={item.id}>
          <MilestoneItem
            key={i}
            item={item}
            onChange={handleChange}
            onDelete={
              item.type === 'diet' || (item.type === 'enforcement' && enforcements.length > 1)
                ? handleDelete
                : undefined
            }
            onRestore={item.type === 'diet' ? handleRestore : undefined}
            documentAutocomplete={filteredDocumentAutoComplete}
          />
          {i === lastEnforcementIndex && (
            <Button
              onClick={() => {
                setItems([
                  ...items.slice(0, lastEnforcementIndex + 1),
                  {
                    id: MilestoneId.parse(uuidv7()),
                    type: 'preparation',
                    name: '施行に向けた準備',
                    description: null,
                    documents: [],
                    disabled: false,
                  },
                  {
                    id: MilestoneId.parse(uuidv7()),
                    type: 'enforcement',
                    name: '施行',
                    description: null,
                    documents: [],
                    disabled: false,
                  },
                  ...items.slice(lastEnforcementIndex + 1),
                ] satisfies Array<ManagedMilestone>);
              }}
              variant="outlined"
            >
              <FlagOutlined sx={{ marginRight: 1 }} />
              施行を追加
            </Button>
          )}
        </Fragment>
      ))}
    </Stack>
  );
};

export const ReadOnlyMilestoneListField = ({ value }: { value: Array<Milestone> }) => {
  const items = useMemo(() => parseManagedMilestones(value), [value]);

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      {items.map((item, i) => (
        <MilestoneItem key={i} item={item} />
      ))}
    </Stack>
  );
};

type MilestoneItemProps = {
  item: ManagedMilestone;
  onChange?: (item: ManagedMilestone) => void;
  onDelete?: (item: ManagedMilestone) => void;
  onRestore?: (item: ManagedMilestone) => void;
  documentAutocomplete?: UseAutocompleteReturnType<TimelineDocumentHead>;
};

const MilestoneItem = ({
  item,
  onDelete,
  onRestore,
  onChange,
  documentAutocomplete,
}: MilestoneItemProps) => {
  const disabled = item.disabled;

  return (
    <ReadOnlyField label="マイルストーン" disabled={disabled}>
      <Stack>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: disabled ? 'line-through' : undefined,
          }}
          aria-disabled={disabled}
        >
          <FlagOutlined sx={{ marginRight: 1 }} />
          <Box flex={1}>
            <Typography>{item.name}</Typography>
          </Box>
          {(onDelete || onRestore) && (
            <Button
              color={disabled ? 'info' : 'error'}
              onClick={() => {
                if (disabled) {
                  onRestore?.(item);
                } else {
                  onDelete?.(item);
                }
              }}
              size="small"
              sx={{ minWidth: 'revert' }}
            >
              {disabled ? <RestoreOutlined /> : <DeleteOutline />}
            </Button>
          )}
        </Box>
        {onChange && !disabled ? (
          <Box sx={{ display: 'flex', alignItems: 'center', padding: 2, paddingBottom: 0 }}>
            <DocumentListField
              milestone={item}
              onChange={(changed) => {
                onChange({ ...item, ...changed });
              }}
              documentAutocomplete={documentAutocomplete!}
            />
          </Box>
        ) : (
          item.documents.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', padding: 2, paddingBottom: 0 }}>
              <Box>
                {item.documents.map((document) => (
                  <Box
                    key={document.id}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}
                  >
                    <ArticleOutlined />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        {document.title}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {document.isPinned ? `要チェック: ${document.pinnedLabel}` : null}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )
        )}
      </Stack>
    </ReadOnlyField>
  );
};

const DocumentListField = ({
  milestone,
  onChange,
  documentAutocomplete: { autocompleteProps },
}: {
  milestone: Milestone;
  onChange: (milestone: Milestone) => void;
  documentAutocomplete: UseAutocompleteReturnType<TimelineDocumentHead>;
}) => {
  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      {milestone.type === 'enforcement' && (
        <TextField
          value={milestone.description ?? ''}
          placeholder="説明テキスト"
          onChange={(e) => {
            const value = e.target.value.trim() === '' ? null : e.target.value;

            onChange({
              ...milestone,
              description: value,
            });
          }}
        />
      )}
      {milestone.documents.map((document, index) => {
        return (
          <Box
            key={document.id}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}
          >
            <ArticleOutlined />
            <Autocomplete
              {...autocompleteProps}
              sx={{ flex: 1 }}
              value={document}
              onChange={(_, value) => {
                if (value) {
                  onChange({
                    ...milestone,
                    documents: milestone.documents.map((doc, i) =>
                      i === index
                        ? {
                            ...doc,
                            ...value,
                          }
                        : doc,
                    ),
                  });
                }
              }}
              getOptionKey={(option) => option.id}
              getOptionLabel={(option) =>
                option.type ? `（${DOCUMENT_TYPES[option.type]?.name}）${option.title}` : ''
              }
              filterSelectedOptions
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    label="記事"
                    variant="outlined"
                    size="small"
                    sx={{ flex: 1 }}
                    required
                  />
                );
              }}
            />
            <Checkbox
              sx={{ margin: 0, padding: 0 }}
              checked={document.isPinned}
              onChange={(e) => {
                onChange({
                  ...milestone,
                  documents: milestone.documents.map((doc, i) =>
                    i === index
                      ? {
                          ...doc,
                          isPinned: e.target.checked,
                        }
                      : doc,
                  ),
                });
              }}
            />
            <FormLabel>要チェック</FormLabel>
            {document.isPinned && (
              <TextField
                value={document.pinnedLabel}
                onChange={(e) => {
                  const value = e.target.value.trim() === '' ? null : e.target.value;

                  onChange({
                    ...milestone,
                    documents: milestone.documents.map((doc, i) =>
                      i === index
                        ? {
                            ...doc,
                            pinnedLabel: value,
                          }
                        : doc,
                    ),
                  });
                }}
                label="ラベル"
                variant="outlined"
                size="small"
              />
            )}
            <Button
              color="inherit"
              onClick={() => {
                onChange({
                  ...milestone,
                  documents: milestone.documents.filter((_, i) => i !== index),
                });
              }}
              size="small"
              sx={{ minWidth: 'revert' }}
            >
              <CloseOutlined />
            </Button>
          </Box>
        );
      })}
      <Button
        color="secondary"
        sx={{ justifyContent: 'start', padding: 0, gap: 1 }}
        size="small"
        onClick={() => {
          onChange({
            ...milestone,
            documents: [
              ...milestone.documents,
              {
                id: uuidv7(),
                title: '',
                isPinned: false,
                pinnedLabel: null,
              } as TimelineDocumentHead,
            ],
          });
        }}
      >
        <AddOutlined />
        記事を追加
      </Button>
    </Stack>
  );
};

function parseManagedMilestones(milestones: Array<Milestone>): Array<ManagedMilestone> {
  const items: Array<ManagedMilestone> = [];

  const enforcementCount = milestones.filter((item) => item.type === 'enforcement').length;

  let currentEnforcementIndex = 0;

  for (let i = 0; i < milestones.length; i += 1) {
    const item = milestones[i]!;

    let name = item.name;

    if (item.type === 'conclusion') {
      // 国会審議が存在しない場合があるため補完する
      if (milestones[i - 1]?.type !== 'diet') {
        items.push({
          id: MilestoneId.parse(uuidv7()),
          type: 'diet',
          name: '国会審議',
          description: null,
          documents: [],
          disabled: true,
        });
      }
    } else if (item.type === 'preparation') {
      name =
        enforcementCount > 1
          ? `施行に向けた準備${currentEnforcementIndex + 1}`
          : `施行に向けた準備`;
    } else if (item.type === 'enforcement') {
      const isLastEnforcement = currentEnforcementIndex === enforcementCount - 1;

      if (enforcementCount === 1) {
        name = '施行';
      } else if (enforcementCount === 2) {
        name = isLastEnforcement ? '全面施行' : '一部施行';
      } else {
        name = isLastEnforcement ? '全面施行' : `一部施行${currentEnforcementIndex + 1}`;
      }

      const preparation = items.at(-1);
      if (preparation?.type !== 'preparation') {
        throw new Error('対応する「施行に向けた準備」が見つかりません');
      }

      currentEnforcementIndex += 1;
    }

    items.push({ ...item, name, disabled: false });
  }

  return items;
}
